import * as R from 'ramda';

import { hasValue, isArray, isNotNil, isObject } from './type';

interface SafeProp {
  <T extends {}, K extends keyof T>(key: K, obj: T): T[K] | null;
  <K extends string>(key: K): <T extends Record<K, any>>(obj: T) => T[K] | null;
}

/**
 * Null safe function that returns property value of an object.
 *
 * @param T object type
 * @param K key of the object
 * @param propName name of the property
 * @param obj the object
 * @return T[K]
 */
export const safeProp: SafeProp = R.curry((propName, obj) =>
  hasValue(obj) ? obj[propName] : null
);

interface PropNotNil {
  <T extends {}, K extends keyof T>(key: K, obj: T): T[K] | null;
  <K extends string>(key: K): <T extends Record<K, any>>(obj: T) => T[K] | null;
}

export const propNotNil: PropNotNil = R.curry( <T extends {}, K extends keyof T>(propName: K, obj: T) =>
  R.compose(isNotNil, R.prop(propName))(obj) as T[K]
);

const isObjectOrArray = R.anyPass([isArray, isObject]);
export const toJsonString = (x: any) => isObjectOrArray(x) ? JSON.stringify(x) : x;
export const fromJson = (x: string) => hasValue(x) ? JSON.parse(x) : null;

type ToObjectByIndex = <T>(xs: T[]) => Record<number, T>;
type Reducer = <T, R>(R, T, number) => R;
type ReduceIndexed = <T, R>(reducer: Reducer, initialValue: R) => (xs: T[]) => R;
const reduceIndexed: ReduceIndexed = R.addIndex(R.reduce);

/**
 * Creates an object from an array, which has the array indexes as its keys and the values are the corresponding
 * elements of the array.
 */
export const toObjectByIndex: ToObjectByIndex = reduceIndexed((result, value, index) =>
  R.assoc(index, value, result), {}
);
